import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, Camera } from "lucide-react";
import { LogoBadge } from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO, breadcrumbLd, webPageLd } from "@/lib/seo";

const TITLE = "About DoppelCrush";
const DESC =
  "Why DoppelCrush exists, the team building it, and how we think about playful, selfie-first social discovery.";

export default function About() {
  return (
    <div className="crush-bg min-h-screen" data-testid="about-page">
      <SEO
        title={TITLE}
        description={DESC}
        path="/about"
        jsonLd={[
          webPageLd({ title: TITLE, description: DESC, path: "/about" }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <Link to="/"><LogoBadge /></Link>

        <article className="crush-frame mt-8 p-6 sm:p-10">
          <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95]">
            <span className="crush-text-grad">About</span> DoppelCrush.
          </h1>
          <p className="mt-5 text-slate-700 font-body text-lg">
            DoppelCrush is a playful, selfie-first dating and social discovery app. We&apos;re building
            the most fun way to meet people online — light, weird, and a little bit unhinged.
          </p>

          <h2 className="font-display text-3xl font-bold mt-10">Why we built it</h2>
          <p className="mt-3 text-slate-700 font-body">
            Dating apps got serious. We wanted the opposite: an app where the first thing you do
            is drop a selfie and see who your face card says you&apos;re meant to meet — or who would
            absolutely break your usual algorithm.
          </p>

          <h2 className="font-display text-3xl font-bold mt-10">The two modes</h2>
          <ul className="mt-3 grid sm:grid-cols-2 gap-4">
            <li className="rounded-2xl border-2 border-pink-100 bg-pink-50/50 p-4">
              <div className="inline-flex items-center gap-2 font-display font-bold text-pink-700">
                <Camera className="w-4 h-4" /> Doppel Mode
              </div>
              <p className="text-sm font-body text-slate-700 mt-2">
                Twin energy. The match algorithm ranks faces by similarity to yours — the more you
                look like someone in our world, the higher they appear.
              </p>
            </li>
            <li className="rounded-2xl border-2 border-purple-100 bg-purple-50/50 p-4">
              <div className="inline-flex items-center gap-2 font-display font-bold text-purple-700">
                <Sparkles className="w-4 h-4" /> Chaos Mode
              </div>
              <p className="text-sm font-body text-slate-700 mt-2">
                Plot twist energy. We surface people who land in the &quot;deliberately unlike you&quot;
                contrast band. Wildly more interesting first dates.
              </p>
            </li>
          </ul>

          <h2 className="font-display text-3xl font-bold mt-10">Made for sharing</h2>
          <p className="mt-3 text-slate-700 font-body">
            DoppelCrush is built for the group chat. Drop your reveal, open a <Link to="/doppel-room" className="text-pink-600 underline">Doppel Room</Link> with
            friends, share your code, and watch who gets crowned the strongest twin or the wildest
            chaos pick. We never compare you face-to-face with your friends — your selfie is only
            used to compute <em>your</em> picks.
          </p>

          <h2 className="font-display text-3xl font-bold mt-10">Who it&apos;s for</h2>
          <p className="mt-3 text-slate-700 font-body">
            Adults 18+ who want dating to feel like a TikTok rabbit hole, not a job application.
            Match men, women, or everyone — your preference, your rules.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/auth" className="crush-cta rounded-full font-display font-bold px-6 py-3 inline-flex items-center gap-2" data-testid="about-signup-cta">
              <Users className="w-4 h-4" /> Drop your selfie <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/how-it-works" className="crush-secondary rounded-full font-display font-bold px-6 py-3">How it works →</Link>
            <Link to="/safety" className="crush-secondary rounded-full font-display font-bold px-6 py-3">Safety →</Link>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
